import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink } from "lucide-react";
import AdminLayout from "../../commponents/layout/adminLayout";
import SectionFields from "../../commponents/homePage/SectionFields";
import ItemsEditor from "../../commponents/homePage/ItemsEditor";
import {
  getHomePageSection,
  updateHomePageSection,
} from "../../api/homePageApi";
import { getSectionConfig } from "../../helpers/homePageSections";
import {
  buildSectionFormData,
  createEmptyItem,
  parseSectionData,
  validateSection,
} from "../../helpers/homePageForm";
import {
  clearItemError,
  scrollToField,
  scrollToSection,
  type FieldErrors,
  type ItemsErrors,
} from "../../helpers/validation";
import { showConfirm, showError, showSuccess } from "../../utils/ alert";
import type {
  HomeFieldValue,
  HomeSectionItem,
  HomeSectionValues,
} from "../../type/homePage";

export default function HomePageEdit() {
  const { section } = useParams();
  const navigate = useNavigate();

  const config = getSectionConfig(section);

  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dirty, setDirty] = useState(false);

  const [values, setValues] = useState<HomeSectionValues>({});
  const [items, setItems] = useState<HomeSectionItem[]>([]);

  const [errors, setErrors] = useState<FieldErrors>({});
  const [itemsErrors, setItemsErrors] = useState<ItemsErrors>({});
  const [itemsGeneralError, setItemsGeneralError] = useState<
    string | undefined
  >();

  const fetchSection = useCallback(async () => {
    if (!config) return;

    try {
      setLoading(true);
      setLoadFailed(false);

      const { data } = await getHomePageSection(config.key);
      const parsed = parseSectionData(config, data?.data);

      setValues(parsed.values);
      setItems(parsed.items);
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response
        ?.status;

      if (status === 404) {
        // Section belum pernah disimpan -> mulai dari form kosong.
        const parsed = parseSectionData(config, null);

        setValues(parsed.values);
        setItems(parsed.items);
      } else {
        // Jangan tampilkan form kosong kalau gagal load: kalau admin
        // menekan Save, konten yang sudah ada di website bisa tertimpa.
        setLoadFailed(true);
        showError("Failed", "Failed to Load data.");
      }
    } finally {
      setLoading(false);
    }
  }, [config]);

  useEffect(() => {
    fetchSection();
  }, [fetchSection]);

  const handleBack = async () => {
    if (dirty) {
      const confirmed = await showConfirm(
        "Discard Changes",
        "You have unsaved changes. Are you sure you want to leave this page?"
      );

      if (!confirmed) return;
    }

    navigate("/home-page");
  };

  const handleFieldChange = (key: string, value: HomeFieldValue) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setDirty(true);

    setErrors((prev) => {
      if (!prev[key]) return prev;

      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleItemsChange = (next: HomeSectionItem[]) => {
    setItems(next);
    setDirty(true);
  };

  const handleAddItem = () => {
    if (!config) return;

    setItems((prev) => [...prev, createEmptyItem(config)]);
    setItemsGeneralError(undefined);
    setDirty(true);
  };

  const handleClearItemError = (index?: number, key?: string) => {
    if (index === undefined || key === undefined) {
      // Struktur berubah -> index error lama tidak valid lagi.
      setItemsErrors({});
      setItemsGeneralError(undefined);
      return;
    }

    clearItemError(setItemsErrors, index, key);
  };

  const focusFirstError = (
    fieldErrors: FieldErrors,
    rowErrors: ItemsErrors,
    hasGeneralError: boolean
  ) => {
    const firstFieldKey = Object.keys(fieldErrors)[0];

    if (firstFieldKey) {
      scrollToField(firstFieldKey);
      return;
    }

    const firstRowIndex = Object.keys(rowErrors)[0];

    if (firstRowIndex !== undefined) {
      const firstKey = Object.keys(rowErrors[Number(firstRowIndex)])[0];
      scrollToField(`item-${firstRowIndex}-${firstKey}`);
      return;
    }

    if (hasGeneralError) {
      scrollToSection("home-items-section");
    }
  };

  const handleSave = async () => {
    if (!config) return;

    const result = validateSection(config, values, items);

    setErrors(result.fieldErrors);
    setItemsErrors(result.itemsErrors);
    setItemsGeneralError(result.itemsGeneralError);

    if (result.hasError) {
      focusFirstError(
        result.fieldErrors,
        result.itemsErrors,
        Boolean(result.itemsGeneralError)
      );
      return;
    }

    try {
      setSubmitting(true);

      await updateHomePageSection(
        config.key,
        buildSectionFormData(config, values, items)
      );

      setDirty(false);

      await showSuccess(`${config.title} updated successfully.`);

      navigate("/home-page");
    } catch (error) {
      showError("Failed", "Failed to save data.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!config) {
    return (
      <AdminLayout>
        <div className="rounded-3xl bg-white p-8 shadow-sm">
          <p className="mb-6">Section tidak ditemukan.</p>

          <button
            onClick={() => navigate("/home-page")}
            className="rounded-xl bg-blue-600 px-7 py-3 font-medium text-white hover:bg-blue-700">
            Back to Home Page
          </button>
        </div>
      </AdminLayout>
    );
  }

  if (loading) {
    return (
      <AdminLayout>
        <div className="rounded-3xl bg-white p-8 shadow-sm">Loading...</div>
      </AdminLayout>
    );
  }

  if (loadFailed) {
    return (
      <AdminLayout>
        <div className="rounded-3xl bg-white p-8 shadow-sm">
          <p className="mb-6">
            Data {config.title} gagal dimuat. Form tidak ditampilkan agar
            konten yang sudah ada tidak tertimpa.
          </p>

          <div className="flex gap-4">
            <button
              onClick={fetchSection}
              className="rounded-xl bg-blue-600 px-7 py-3 font-medium text-white hover:bg-blue-700">
              Retry
            </button>

            <button
              onClick={() => navigate("/home-page")}
              className="rounded-xl border border-gray-300 px-7 py-3 font-medium text-gray-700 hover:bg-gray-50">
              Back
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="rounded-3xl bg-white p-8 shadow-sm">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between gap-6">
          <div>
            <h1 className="text-2xl font-bold">Edit {config.title}</h1>

            <p className="mt-1 text-gray-500">{config.description}</p>
          </div>

          <button
            onClick={handleBack}
            className="flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50">
            <ArrowLeft size={16} />
            Back
          </button>
        </div>

        {/* Note */}
        {config.note && (
          <div className="mb-8 flex items-center justify-between gap-6 rounded-xl bg-blue-50 p-5 text-blue-900">
            <p>{config.note.text}</p>

            <button
              onClick={() => navigate(config.note!.path)}
              className="flex shrink-0 items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700">
              {config.note.linkLabel}
              <ExternalLink size={16} />
            </button>
          </div>
        )}

        {/* Section fields */}
        <SectionFields
          fields={config.fields}
          values={values}
          errors={errors}
          onChange={handleFieldChange}
        />

        {/* Repeatable items */}
        {config.items && (
          <div className={config.fields.length > 0 ? "mt-12" : ""}>
            <ItemsEditor
              config={config.items}
              items={items}
              errors={itemsErrors}
              generalError={itemsGeneralError}
              onChange={handleItemsChange}
              onAdd={handleAddItem}
              onClearError={handleClearItemError}
            />
          </div>
        )}

        {/* Footer */}
        <div className="mt-10 flex justify-end gap-4">
          <button
            onClick={handleBack}
            disabled={submitting}
            className="rounded-xl bg-red-600 px-7 py-3 font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60">
            Cancel
          </button>

          <button
            onClick={handleSave}
            disabled={submitting}
            className="rounded-xl bg-green-600 px-7 py-3 font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60">
            {submitting ? "Saving..." : "Update"}
          </button>
        </div>
      </div>
    </AdminLayout>
  );
}
